import { useSelector } from 'react-redux';
import { useMemo, useState } from 'react';
import { AGE_GROUPS } from '../../utils/constants';
import { getRumaalColor } from '../../utils/colors';
import LocationSwitcher from '../location/LocationSwitcher';
import WeekSelector from './WeekSelector';
import { getMondayOfWeek, isDateInWeek } from '../../utils/timeUtils';

// Helper function to remove skill level prefixes from class names
// Removes: Beg, Int, Adv, Beginner, Intermediate, Advanced
const cleanClassName = (className, type, ageGroup) => {
  if (!className) {
    return `${type} – ${ageGroup}`;
  }
  
  // Remove skill level prefixes (case-insensitive)
  const cleaned = className
    .replace(/^(Advanced|Beginner|Intermediate|Beg|Int|Adv)\s+/i, '')
    .trim();
  
  // If after cleaning the name is empty or just whitespace, use fallback
  if (!cleaned) {
    return `${type} – ${ageGroup}`;
  }
  
  return cleaned;
};

// Helper function to get current week's date range (Monday to Sunday)
const getCurrentWeekRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  // Calculate days to Monday (if today is Sunday, go back 6 days; otherwise go back dayOfWeek - 1)
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysToMonday);
  monday.setHours(0, 0, 0, 0); // Start of Monday
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999); // End of Sunday
  
  return { start: monday, end: sunday };
};

// Helper function to check if a date is within the current week
const isInCurrentWeek = (dateString) => {
  if (!dateString) return false;
  const { start, end } = getCurrentWeekRange();
  const date = new Date(dateString);
  return date >= start && date <= end;
};

const WeeklyStats = () => {
  const { classes } = useSelector((state) => state.classes);
  const { selectedLocation } = useSelector((state) => state.calendar);
  const [isExpanded, setIsExpanded] = useState(true);
  const [modalData, setModalData] = useState(null); // { type: 'joined'|'canceled'|'paused'|'trials', data: [] }
  const [selectedWeekMonday, setSelectedWeekMonday] = useState(() => getMondayOfWeek());

  // Calculate stats from actual class data
  const stats = useMemo(() => {
    // Filter classes based on selected location
    const filteredClasses = selectedLocation === null 
      ? classes 
      : classes.filter((c) => c.location === selectedLocation);
    
    // Get all students from all classes with instructor info
    const allStudents = filteredClasses.flatMap((classItem) => 
      (classItem.students || []).map(student => ({
        ...student,
        ageGroup: classItem.ageGroup,
        instructor: classItem.instructor,
        className: cleanClassName(classItem.name, classItem.type, classItem.ageGroup),
        classCreatedAt: classItem.createdAt, // For fallback when student doesn't have addedAt
      }))
    );

    // Helper function to get student added date (use addedAt if available, otherwise class createdAt)
    const getStudentAddedDate = (student) => {
      if (student.addedAt) {
        return new Date(student.addedAt);
      }
      if (student.classCreatedAt) {
        return new Date(student.classCreatedAt);
      }
      return null;
    };

    // Students Added: Students added during the selected week
    const studentsAdded = allStudents.filter((s) => {
      const addedDate = getStudentAddedDate(s);
      return addedDate && isDateInWeek(addedDate, selectedWeekMonday);
    }).length;

    // Total L2B Students: Active + Cash only (real academy size) - current snapshot
    const activeStudents = allStudents.filter((s) => s.status === 'Active').length;
    const cashStudents = allStudents.filter((s) => s.status === 'Cash').length;
    const totalL2BStudents = activeStudents + cashStudents;

    // Total in system: All students (Active + Cash + Trial + Paused) - current snapshot
    const trialStudents = allStudents.filter((s) => s.status === 'Trial').length;
    const pausedStudents = allStudents.filter((s) => s.status === 'Paused').length;
    const totalInSystem = activeStudents + cashStudents + trialStudents + pausedStudents;

    // Paused Students: Students whose status changed to Paused during the selected week
    const pausedStudentsThisWeek = allStudents.filter((s) => {
      if (s.status !== 'Paused' || !s.pausedAt) return false;
      return isDateInWeek(new Date(s.pausedAt), selectedWeekMonday);
    }).length;

    // Rumaal Upgrades: Students who were upgraded during the selected week
    const rumaalUpgrades = allStudents.filter((s) => {
      if (!s.rumaalUpgradedAt) return false;
      return isDateInWeek(new Date(s.rumaalUpgradedAt), selectedWeekMonday);
    }).length;

    // Recent Rumaal Upgrades: Last 5 upgrades during selected week
    const recentRumaalUpgrades = allStudents
      .filter((s) => {
        if (!s.rumaalUpgradedAt) return false;
        return isDateInWeek(new Date(s.rumaalUpgradedAt), selectedWeekMonday);
      })
      .sort((a, b) => new Date(b.rumaalUpgradedAt) - new Date(a.rumaalUpgradedAt))
      .slice(0, 5)
      .map((s) => ({
        studentName: s.name,
        instructor: s.rumaalUpgradedBy || s.instructor || 'Unknown',
        upgradedAt: s.rumaalUpgradedAt,
        previousRank: s.previousRumaalRank || 'Unknown',
        currentRank: s.rumaalRank,
        className: s.className,
      }));

    // Trials Booked: Students with Trial status added during the selected week
    const trialsBooked = allStudents.filter((s) => {
      if (s.status !== 'Trial') return false;
      const addedDate = getStudentAddedDate(s);
      return addedDate && isDateInWeek(addedDate, selectedWeekMonday);
    }).length;

    // Trial Stage Metrics: Track trial flow through stages
    // In Progress: Trials added during the week that are still in "In Progress" stage
    const trialsInProgress = allStudents.filter((s) => {
      if (s.status !== 'Trial' || s.trialStage !== 'In Progress') return false;
      const addedDate = getStudentAddedDate(s);
      return addedDate && isDateInWeek(addedDate, selectedWeekMonday);
    }).length;

    // Attending: Trials that moved to "Attending" stage during the selected week
    const trialsAttending = allStudents.filter((s) => {
      if (s.status !== 'Trial' || !s.trialAttendedAt) return false;
      return isDateInWeek(new Date(s.trialAttendedAt), selectedWeekMonday);
    }).length;

    // Attended: Trials that moved to "Attended" stage during the selected week
    const trialsAttended = allStudents.filter((s) => {
      if (s.status !== 'Trial' || !s.trialCompletedAt) return false;
      return isDateInWeek(new Date(s.trialCompletedAt), selectedWeekMonday);
    }).length;

    // Trials Converted: Students who were converted from Trial to Active during the selected week
    const trialsConverted = allStudents.filter((s) => {
      if (!s.convertedAt || s.status !== 'Active') return false;
      return isDateInWeek(new Date(s.convertedAt), selectedWeekMonday);
    }).length;

    // Recent Conversions: Last 5 conversions during selected week
    const recentConversions = allStudents
      .filter((s) => {
        if (!s.convertedAt || s.status !== 'Active') return false;
        return isDateInWeek(new Date(s.convertedAt), selectedWeekMonday);
      })
      .sort((a, b) => new Date(b.convertedAt) - new Date(a.convertedAt))
      .slice(0, 5)
      .map((s) => ({
        studentName: s.name,
        instructor: s.convertedBy || s.instructor || 'Unknown',
        convertedAt: s.convertedAt,
        className: s.className,
      }));

    // Conversion Rate: Actual Conversions / (Trials Booked + Actual Conversions)
    // This shows: How many trials converted out of all trial-related students
    const totalTrials = trialsBooked + trialsConverted;
    const conversionRate = totalTrials > 0 
      ? Math.round((trialsConverted / totalTrials) * 100)
      : 0;

    // Category Breakdown: Count students added during selected week by age group
    const categoryBreakdown = allStudents
      .filter((s) => {
        const addedDate = getStudentAddedDate(s);
        return addedDate && isDateInWeek(addedDate, selectedWeekMonday);
      })
      .reduce((acc, student) => {
        const ageGroup = student.ageGroup || 'Unknown';
        acc[ageGroup] = (acc[ageGroup] || 0) + 1;
        return acc;
      }, {});

    // Ensure all age groups are represented (even with 0)
    AGE_GROUPS.forEach((group) => {
      if (!categoryBreakdown[group]) {
        categoryBreakdown[group] = 0;
      }
    });

    // 1. Net Student Change: (Students Added) - (Paused Students)
    // This answers: "Did we actually grow this week?"
    const netStudentChange = studentsAdded - pausedStudentsThisWeek;

    // 2. Classes at Risk: Classes with ≤3 students
    const classesAtRisk = filteredClasses.filter((classItem) => {
      const studentCount = (classItem.students || []).length;
      return studentCount <= 3;
    });
    const classesAtRiskCount = classesAtRisk.length;
    // Get breakdown by day for subtext
    const riskByDay = classesAtRisk.reduce((acc, classItem) => {
      const day = classItem.day || 'Unknown';
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});
    const topRiskDay = Object.entries(riskByDay).sort((a, b) => b[1] - a[1])[0];
    const riskSubtext = topRiskDay ? `Mostly ${topRiskDay[0]} classes` : '';

    // 3. Capacity Momentum: Classes that are 75%+ full (9/12+)
    const capacity = 12; // Target capacity per class
    const classesNearFull = filteredClasses.filter((classItem) => {
      const studentCount = (classItem.students || []).length;
      return studentCount >= 9; // 75% of 12
    });
    const classesNearFullCount = classesNearFull.length;

    // 4. Admin Impact: Count of actions during selected week
    // Actions include: Students added, Trials converted, Rumaal upgrades
    const adminImpact = studentsAdded + trialsConverted + rumaalUpgrades;

    // 5. Class Movement Score: Students moved into higher rumaal classes
    // This counts students who:
    // - Were upgraded (rumaalUpgradedAt exists)
    // - Are in classes with higher rumaal levels than their previous rank
    const rankOrder = ['White', 'Yellow', 'Green', 'Blue', 'Red', 'Black'];
    const getRankIndex = (rank) => rankOrder.indexOf(rank) || 0;
    
    let classMovementScore = 0;
    // Count rumaal upgrades (students moved to higher ranks)
    classMovementScore += rumaalUpgrades;
    
    // Also count students in classes where class rumaal level is higher than student rank
    // This indicates progression/consolidation
    filteredClasses.forEach((classItem) => {
      const classRumaalLevel = classItem.rumaalLevel || 'White';
      const classRankIndex = getRankIndex(classRumaalLevel);
      
      (classItem.students || []).forEach((student) => {
        const studentRankIndex = getRankIndex(student.rumaalRank || 'White');
        // If class level is higher, this represents good movement/consolidation
        if (classRankIndex > studentRankIndex) {
          classMovementScore += 0.5; // Partial credit for being in a higher-level class
        }
      });
    });
    // Round to nearest integer for display
    classMovementScore = Math.round(classMovementScore);

    return {
      studentsAdded,
      totalL2BStudents,
      totalInSystem,
      rumaalUpgrades,
      trialsBooked,
      trialsConverted,
      conversionRate,
      categoryBreakdown,
      recentConversions,
      recentRumaalUpgrades,
      // Weekly stats
      netStudentChange,
      classesAtRiskCount,
      riskSubtext,
      classesNearFullCount,
      adminImpact,
      classMovementScore,
      pausedStudentsThisWeek,
      // Trial stage metrics
      trialsInProgress,
      trialsAttending,
      trialsAttended,
    };
  }, [classes, selectedLocation, selectedWeekMonday]);

  const categoryColors = {
    'Littles': '#FEF3C7', // Light yellow
    'Kids': '#FDBA74', // Rich orange - deeper and more vibrant
    'Teens': '#D1FAE5', // Fresh mint
    'Young Adults': '#DBEAFE', // Sky blue
    'Adults': '#FCA5A5', // Rich red - deeper and more vibrant
  };

  const categoryTextColors = {
    'Littles': '#78350F', // Darker yellow/brown
    'Kids': '#C2410C', // Darker rich orange
    'Teens': '#065F46', // Darker green
    'Young Adults': '#1E40AF', // Darker blue
    'Adults': '#B91C1C', // Darker rich red
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        {/* Header */}
        <div className="mb-4 sm:mb-5">
          <div className={`relative bg-gradient-to-r from-orange-50 via-amber-50/30 to-orange-50 rounded-2xl p-4 sm:p-5 border border-orange-100/50 shadow-sm transition-all duration-500 ease-in-out ${
            isExpanded ? 'shadow-md' : 'shadow-sm'
          }`}>
            <div className="flex items-center justify-between">
              {/* L2B Logo */}
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16">
                <div className="w-full h-full rounded-xl bg-white shadow-md flex items-center justify-center border-2 border-orange-200/50 p-1.5 overflow-hidden">
                  <img 
                    src="/l2blogo.png" 
                    alt="Learn2Bhangra" 
                    className="h-full w-full object-contain rounded-xl"
                    onError={(e) => {
                      console.error('Logo failed to load');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
              
              {/* Title Section - Centered */}
              <div className="flex-1 flex flex-col items-center justify-center mx-4">
                <div className="flex items-baseline gap-2 flex-wrap justify-center">
                  <span className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-700">Weekly Stats</span>
                  <span className="text-lg sm:text-xl md:text-2xl font-bold text-orange-600">Learn2Bhangra</span>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">Performance Analytics</span>
                </div>
              </div>
              
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex-shrink-0 p-2.5 rounded-xl bg-white/80 hover:bg-white border border-orange-200/50 hover:border-orange-300 transition-all duration-300 hover:scale-110 active:scale-95 shadow-sm hover:shadow-md"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform duration-700 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                  style={{
                    transitionTimingFunction: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)', // Elastic bounce
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Week Selector */}
        <div className="mb-3 sm:mb-4">
          <WeekSelector 
            selectedWeekMonday={selectedWeekMonday}
            onWeekChange={setSelectedWeekMonday}
          />
        </div>

        {/* Location Switcher */}
        <div className="mb-3 sm:mb-4">
          <LocationSwitcher />
        </div>
      
        {/* Animated Content */}
        <div
          className={`overflow-hidden transition-all ${
            isExpanded
              ? 'max-h-[5000px] opacity-100'
              : 'max-h-0 opacity-0'
          }`}
          style={{
            transitionDuration: isExpanded ? '800ms' : '600ms',
            transitionTimingFunction: isExpanded 
              ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' // Spring-like bounce
              : 'cubic-bezier(0.4, 0, 0.2, 1)', // Smooth ease-out
            transitionProperty: 'max-height, opacity',
          }}
        >
          <div 
            className={`transform transition-all ${
              isExpanded 
                ? 'translate-y-0 opacity-100 scale-100 blur-0' 
                : '-translate-y-8 opacity-0 scale-95 blur-sm'
            }`}
            style={{
              transitionDuration: isExpanded ? '800ms' : '400ms',
              transitionTimingFunction: isExpanded
                ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' // Spring bounce
                : 'cubic-bezier(0.4, 0, 0.2, 1)',
              transitionDelay: isExpanded ? '50ms' : '0ms',
              transitionProperty: 'transform, opacity, filter',
            }}
          >
        {/* KPI Cards Grid - Row 1: Original Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-2 sm:mb-3">
          {/* Total L2B Students */}
          <div 
            className={`bg-white border border-gray-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '200ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-600 mb-0.5">Total L2B Students</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalL2BStudents}</div>
            <div className="text-xs text-gray-500 mt-1">Total in system: {stats.totalInSystem}</div>
          </div>

          {/* Rumaal Upgrades */}
          <div 
            onClick={() => setModalData({ 
              type: 'upgrades', 
              title: 'Rumaal Upgrades This Week',
              data: stats.rumaalUpgradesThisWeekList,
            })}
            className={`bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 cursor-pointer hover:shadow-md hover:scale-105 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '250ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">Rumaal Upgrades</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.rumaalUpgradesThisWeek}</div>
            <div className="text-xs text-gray-500 mt-1">Click to see details</div>
          </div>

          {/* Trials Booked */}
          <div 
            onClick={() => setModalData({ 
              type: 'trials', 
              title: 'Trials Booked This Week',
              data: stats.trialsBookedThisWeekList,
            })}
            className={`bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 cursor-pointer hover:shadow-md hover:scale-105 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '300ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">Trials Booked</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.trialsBookedThisWeek}</div>
            <div className="text-xs text-gray-500 mt-1">Click to see details</div>
          </div>

          {/* Trials Converted */}
          <div 
            onClick={() => setModalData({ 
              type: 'conversions', 
              title: 'Trials Converted This Week',
              data: stats.trialsConvertedThisWeekList,
            })}
            className={`bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 cursor-pointer hover:shadow-md hover:scale-105 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '350ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">Trials Converted</div>
            <div className="flex items-baseline gap-2">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.trialsConvertedThisWeek}</div>
              {stats.trialsBookedThisWeek + stats.trialsConvertedThisWeek > 0 && (
                <div className="text-sm sm:text-base font-semibold text-green-600">
                  {Math.round((stats.trialsConvertedThisWeek / (stats.trialsBookedThisWeek + stats.trialsConvertedThisWeek)) * 100)}%
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">Click to see details</div>
          </div>

          {/* New Students */}
          <div 
            className={`bg-indigo-50 border border-indigo-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '375ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">New Students</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.studentsAdded}</div>
            <div className="text-xs text-gray-500 mt-1">added this week</div>
          </div>

          {/* Paused Students */}
          <div 
            className={`bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '400ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">Paused Students</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.pausedStudentsThisWeek}</div>
            <div className="text-xs text-gray-500 mt-1">paused this week</div>
          </div>
        </div>

        {/* KPI Cards Grid - Row 2: Trial Stage Tracking */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2 sm:gap-3 mb-2 sm:mb-3">
          {/* Trials In Progress */}
          <div 
            className={`bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '425ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">Trials In Progress</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.trialsInProgress}</div>
            <div className="text-xs text-gray-500 mt-1">booked, not attended</div>
          </div>

          {/* Trials Attending */}
          <div 
            className={`bg-amber-50 border border-amber-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '450ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">Trials Attending</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.trialsAttending}</div>
            <div className="text-xs text-gray-500 mt-1">trial attended</div>
          </div>

          {/* Trials Attended */}
          <div 
            className={`bg-teal-50 border border-teal-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '475ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-teal-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 mb-0.5">Trials Attended</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.trialsAttended}</div>
            <div className="text-xs text-gray-500 mt-1">trial completed</div>
          </div>
        </div>

        {/* KPI Cards Grid - Row 3: New High-Value Stats */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 mb-3 sm:mb-4">
          {/* Net Student Change */}
          <div 
            className={`bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '500ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-green-200 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-green-700 mb-0.5">Net Student Change</div>
            <div className={`text-xl sm:text-2xl font-bold ${stats.netStudentChange >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              {stats.netStudentChange >= 0 ? '+' : ''}{stats.netStudentChange}
            </div>
            <div className={`text-xs mt-1 ${stats.netStudentChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.netStudentChange >= 0 ? 'Growth this week' : 'Decline this week'}
            </div>
            <div className="text-xs text-gray-500 mt-1">Click to see details</div>
          </div>

          {/* Paused This Week */}
          <div 
            onClick={() => setModalData({ 
              type: 'paused', 
              title: 'Paused This Week',
              data: stats.studentsPausedThisWeekList,
            })}
            className={`bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 cursor-pointer hover:shadow-md hover:scale-105 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '425ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-red-700 mb-0.5">Paused This Week</div>
            <div className="text-xl sm:text-2xl font-bold text-red-700">{stats.studentsPausedThisWeek}</div>
            <div className="text-xs text-red-600 mt-1">Click to see details</div>
          </div>

          {/* Classes at Risk */}
          <div 
            className={`bg-orange-50 border border-orange-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '550ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-orange-700 mb-0.5">Classes at Risk</div>
            <div className="text-xl sm:text-2xl font-bold text-orange-700">{stats.classesAtRiskCount}</div>
            {stats.riskSubtext && (
              <div className="text-xs text-orange-600 mt-1">{stats.riskSubtext}</div>
            )}
          </div>

          {/* Capacity Momentum */}
          <div 
            className={`bg-emerald-50 border border-emerald-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '600ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-emerald-700 mb-0.5">Classes Near Full</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.classesNearFullCount}</div>
            <div className="text-xs text-emerald-600 mt-1">75%+ capacity</div>
          </div>

          {/* Admin Impact */}
          <div 
            className={`bg-cyan-50 border border-cyan-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '650ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-cyan-700 mb-0.5">Admin Impact</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.adminImpact}</div>
            <div className="text-xs text-cyan-600 mt-1">actions this week</div>
          </div>

          {/* Class Movement Score */}
          <div 
            className={`bg-violet-50 border border-violet-200 rounded-lg p-2 sm:p-3 shadow-sm transition-all duration-600 ${
              isExpanded 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-95'
            }`}
            style={{
              transitionDelay: isExpanded ? '700ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-violet-100 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-violet-700 mb-0.5">Class Movement</div>
            <div className={`text-xl sm:text-2xl font-bold ${stats.classMovementScore >= 0 ? 'text-violet-900' : 'text-gray-600'}`}>
              {stats.classMovementScore >= 0 ? '+' : ''}{stats.classMovementScore}
            </div>
            <div className="text-xs text-violet-600 mt-1">progression score</div>
          </div>
        </div>

        {/* Bottom Section: Category Breakdown, Recent Conversions, and Recent Upgrades */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
          {/* Category Growth Breakdown */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Category Growth Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(stats.categoryBreakdown)
                .filter(([category]) => AGE_GROUPS.includes(category))
                .sort(([a], [b]) => {
                  return AGE_GROUPS.indexOf(a) - AGE_GROUPS.indexOf(b);
                })
                .map(([category, count]) => {
                  const maxCount = Math.max(...Object.values(stats.categoryBreakdown), 1);
                  return (
                    <div key={category} className="flex items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-base shadow-sm"
                        style={{ 
                          backgroundColor: categoryColors[category] || '#F3F4F6',
                          color: categoryTextColors[category] || '#374151'
                        }}
                      >
                        {count > 0 ? `+${count}` : '0'}
                      </div>
                      <div className="flex-1">
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${(count / maxCount) * 100}%`,
                              backgroundColor: categoryColors[category] || '#F3F4F6'
                            }}
                          />
                        </div>
                      </div>
                      <div className="text-xs font-medium text-gray-700 min-w-[80px] sm:min-w-[100px]">{category}</div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Recent Conversions */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 shadow-sm">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Recent Conversions</h3>
            {stats.recentConversions.length > 0 ? (
              <div className="space-y-2">
                {stats.recentConversions.map((conversion, index) => (
                  <div 
                    key={index} 
                    className="bg-white rounded-lg p-3 border border-green-200 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-gray-900 mb-1">
                          {conversion.studentName}
                        </div>
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">Instructor:</span> {conversion.instructor}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {conversion.className}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 ml-2">
                        {conversion.convertedAt 
                          ? new Date(conversion.convertedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })
                          : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                No conversions yet. Conversions are tracked when a student changes from Trial to Active.
              </div>
            )}
            {stats.recentConversions.length > 0 && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <div className="text-xs text-gray-600">
                  <span className="font-semibold">Total Conversions:</span> {stats.trialsConverted} 
                  {' • '}
                  <span className="font-semibold">Rate:</span> {stats.conversionRate}%
                </div>
              </div>
            )}
          </div>

          {/* Recent Rumaal Upgrades */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 shadow-sm">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Recent Rumaal Upgrades</h3>
            {stats.recentRumaalUpgrades.length > 0 ? (
              <div className="space-y-2">
                {stats.recentRumaalUpgrades.map((upgrade, index) => (
                  <div 
                    key={index} 
                    className="bg-white rounded-lg p-3 border border-yellow-200 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-gray-900 mb-1">
                          {upgrade.studentName}
                        </div>
                        <div className="text-xs text-gray-600 mb-1">
                          <span className="font-medium">Instructor:</span> {upgrade.instructor}
                        </div>
                        <div className="flex items-center gap-2 text-xs mt-1.5">
                          <div 
                            className="px-2.5 py-1 rounded-full font-semibold text-xs shadow-sm border border-gray-200"
                            style={{ 
                              backgroundColor: getRumaalColor(upgrade.previousRank),
                              color: upgrade.previousRank === 'White' || upgrade.previousRank === 'Yellow' ? '#1F2937' : '#FFFFFF'
                            }}
                          >
                            {upgrade.previousRank}
                          </div>
                          <span className="text-gray-400 font-medium">→</span>
                          <div 
                            className="px-2.5 py-1 rounded-full font-semibold text-xs shadow-sm border border-gray-200"
                            style={{ 
                              backgroundColor: getRumaalColor(upgrade.currentRank),
                              color: upgrade.currentRank === 'White' || upgrade.currentRank === 'Yellow' ? '#1F2937' : '#FFFFFF'
                            }}
                          >
                            {upgrade.currentRank}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {upgrade.className}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 ml-2">
                        {upgrade.upgradedAt 
                          ? new Date(upgrade.upgradedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })
                          : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                No upgrades yet. Upgrades are tracked when a student's Rumaal rank increases.
              </div>
            )}
            {stats.recentRumaalUpgrades.length > 0 && (
              <div className="mt-3 pt-3 border-t border-yellow-200">
                <div className="text-xs text-gray-600">
                  <span className="font-semibold">Total Upgrades:</span> {stats.rumaalUpgrades}
                </div>
              </div>
            )}
          </div>
        </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {modalData && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setModalData(null)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">{modalData.title}</h3>
              <button
                onClick={() => setModalData(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {modalData.type === 'netChange' ? (
              <div className="space-y-4">
                {/* Joined This Week */}
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2">
                    Joined This Week ({modalData.joined?.length || 0})
                  </h4>
                  {modalData.joined && modalData.joined.length > 0 ? (
                    <div className="space-y-2">
                      {modalData.joined.map((item, index) => (
                        <div key={index} className="bg-green-50 border border-green-200 rounded p-3">
                          <div className="font-medium text-gray-900">{item.studentName}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="font-medium">Class:</span> {item.className}
                            {' • '}
                            <span className="font-medium">Instructor:</span> {item.instructor}
                            {' • '}
                            <span className="font-medium">Status:</span> {item.status}
                          </div>
                          {item.addedAt && (
                            <div className="text-xs text-gray-500 mt-1">
                              Added: {new Date(item.addedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No students joined this week.</p>
                  )}
                </div>

                {/* Canceled This Week */}
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">
                    Canceled This Week ({modalData.canceled?.length || 0})
                  </h4>
                  {modalData.canceled && modalData.canceled.length > 0 ? (
                    <div className="space-y-2">
                      {modalData.canceled.map((item, index) => (
                        <div key={index} className="bg-red-50 border border-red-200 rounded p-3">
                          <div className="font-medium text-gray-900">{item.studentName}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="font-medium">Class:</span> {item.className}
                            {' • '}
                            <span className="font-medium">Instructor:</span> {item.instructor}
                            {' • '}
                            <span className="font-medium">Previous Status:</span> {item.previousStatus}
                          </div>
                          {item.canceledAt && (
                            <div className="text-xs text-gray-500 mt-1">
                              Canceled: {new Date(item.canceledAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No students canceled this week.</p>
                  )}
                </div>

                {/* Paused This Week */}
                <div>
                  <h4 className="text-sm font-semibold text-orange-700 mb-2">
                    Paused This Week ({modalData.paused?.length || 0})
                  </h4>
                  {modalData.paused && modalData.paused.length > 0 ? (
                    <div className="space-y-2">
                      {modalData.paused.map((item, index) => (
                        <div key={index} className="bg-orange-50 border border-orange-200 rounded p-3">
                          <div className="font-medium text-gray-900">{item.studentName}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="font-medium">Class:</span> {item.className}
                            {' • '}
                            <span className="font-medium">Instructor:</span> {item.instructor}
                          </div>
                          {item.pausedAt && (
                            <div className="text-xs text-gray-500 mt-1">
                              Paused: {new Date(item.pausedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No students paused this week.</p>
                  )}
                </div>
              </div>
            ) : modalData.type === 'trials' ? (
              <div>
                {modalData.data && modalData.data.length > 0 ? (
                  <div className="space-y-2">
                    {modalData.data.map((item, index) => (
                      <div key={index} className="bg-blue-50 border border-blue-200 rounded p-3">
                        <div className="font-medium text-gray-900">{item.studentName}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Class:</span> {item.className}
                          {' • '}
                          <span className="font-medium">Instructor:</span> {item.instructor}
                        </div>
                        {item.addedAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            Booked: {new Date(item.addedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No trials booked this week.</p>
                )}
              </div>
            ) : modalData.type === 'paused' ? (
              <div>
                {modalData.data && modalData.data.length > 0 ? (
                  <div className="space-y-2">
                    {modalData.data.map((item, index) => (
                      <div key={index} className="bg-red-50 border border-red-200 rounded p-3">
                        <div className="font-medium text-gray-900">{item.studentName}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Class:</span> {item.className}
                          {' • '}
                          <span className="font-medium">Instructor:</span> {item.instructor}
                        </div>
                        {item.pausedAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            Paused: {new Date(item.pausedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No students paused this week.</p>
                )}
              </div>
            ) : modalData.type === 'upgrades' ? (
              <div>
                {modalData.data && modalData.data.length > 0 ? (
                  <div className="space-y-2">
                    {modalData.data.map((item, index) => (
                      <div key={index} className="bg-yellow-50 border border-yellow-200 rounded p-3">
                        <div className="font-medium text-gray-900">{item.studentName}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Class:</span> {item.className}
                          {' • '}
                          <span className="font-medium">Instructor:</span> {item.instructor}
                        </div>
                        <div className="flex items-center gap-2 text-xs mt-2">
                          <div 
                            className="px-2 py-1 rounded-full font-semibold text-xs shadow-sm border border-gray-200"
                            style={{ 
                              backgroundColor: getRumaalColor(item.previousRank),
                              color: item.previousRank === 'White' || item.previousRank === 'Yellow' ? '#1F2937' : '#FFFFFF'
                            }}
                          >
                            {item.previousRank}
                          </div>
                          <span className="text-gray-400 font-medium">→</span>
                          <div 
                            className="px-2 py-1 rounded-full font-semibold text-xs shadow-sm border border-gray-200"
                            style={{ 
                              backgroundColor: getRumaalColor(item.currentRank),
                              color: item.currentRank === 'White' || item.currentRank === 'Yellow' ? '#1F2937' : '#FFFFFF'
                            }}
                          >
                            {item.currentRank}
                          </div>
                        </div>
                        {item.upgradedAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            Upgraded: {new Date(item.upgradedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No upgrades this week.</p>
                )}
              </div>
            ) : modalData.type === 'conversions' ? (
              <div>
                {modalData.data && modalData.data.length > 0 ? (
                  <div className="space-y-2">
                    {modalData.data.map((item, index) => (
                      <div key={index} className="bg-green-50 border border-green-200 rounded p-3">
                        <div className="font-medium text-gray-900">{item.studentName}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Class:</span> {item.className}
                          {' • '}
                          <span className="font-medium">Instructor:</span> {item.instructor}
                        </div>
                        {item.convertedAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            Converted: {new Date(item.convertedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No conversions this week.</p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyStats;

